import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationAdminEntry, NotificationChannelKey } from '../notification-diagnostics/graphql';
import {
  channelCellLabelKey,
  channelCellSeverity,
  DELIVERY_CHANNEL_VALUES,
  TagSeverity
} from '../notification-diagnostics/notification-labels';

/** Ein Platz in der Kanalspalte. `present: false` heisst: der Typ kennt den Kanal nicht. */
interface ChannelCell {
  channel: NotificationChannelKey;
  present: boolean;
  labelKey: string;
  severity: TagSeverity;
}

/**
 * Eine Zeile der Nachrichtenliste (#737) — die Eltern-Ebene.
 *
 * Reine Praesentation: haelt keinen Zustand, stellt keine Query. Die drei Kanalplaetze sind die
 * Zusammenfassung ihrer Kinder; die Kanalnamen stehen in der Kopfzeile der Liste, nicht in der
 * Zelle.
 *
 * Bewusst NICHT standalone — siehe .github/copilot-instructions.md.
 */
@Component({
  selector: 'div[notification-admin-row], app-notification-admin-row',
  templateUrl: './notification-admin-row.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'block bg-white border-b border-gray'
  }
})
export class NotificationAdminRowComponent {

  private readonly router = inject(Router);

  readonly entry = input.required<NotificationAdminEntry>();

  /** Aufgeloester Empfaengername; ist er nicht da, bleibt die UUID stehen. */
  readonly recipientName = input<string | null>(null);

  /**
   * Hier ist `id` die NOTIFICATION-ID — der Drilldown geht direkt darueber. In der Zustellliste
   * ist `id` die Delivery-ID und der Umweg ueber notificationId noetig.
   */
  readonly detailLink = computed<string[]>(() => [
    '/settings/notifications/notification',
    this.entry().id
  ]);

  readonly recipientDisplay = computed(() => this.recipientName() || this.entry().recipientUserId);

  /**
   * Immer drei Plaetze in fester Reihenfolge, damit die Spalten ueber alle Zeilen untereinander
   * stehen. Fehlt ein Kanal im Array, kennt der Typ ihn nicht: Platz bleibt leer, keine Kachel
   * erfinden.
   */
  readonly cells = computed<ChannelCell[]>(() => {
    const channels = this.entry().channels ?? [];
    return DELIVERY_CHANNEL_VALUES.map((channel) => {
      const state = channels.find((candidate) => candidate.channel === channel);
      if (!state) {
        return { channel, present: false, labelKey: '', severity: 'info' as TagSeverity };
      }
      return {
        channel,
        present: true,
        labelKey: channelCellLabelKey(state.status, state.decisionReason),
        severity: channelCellSeverity(state.status, state.decisionReason)
      };
    });
  });

  /** Loeschen ist der staerkere Zustand und setzt readAt mit — deshalb nie beides. */
  readonly readStateKey = computed(() => {
    const entry = this.entry();
    if (entry.deletedAt) {
      return 'core.notificationDiagnostics.messages.deleted';
    }
    return entry.readAt
      ? 'core.notificationDiagnostics.messages.read'
      : 'core.notificationDiagnostics.messages.unread';
  });

  onRowClick(event: MouseEvent): void {
    const link = this.detailLink();
    // Support arbeitet faellenweise und oeffnet gern mehrere Zeilen nebeneinander.
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      window.open(this.router.serializeUrl(this.router.createUrlTree(link)), '_blank');
      return;
    }
    this.router.navigate(link);
  }
}
